import { keycloakConfig, TOKEN_CONFIG } from "../config/keycloak";
import { generateCodeChallenge, generateCodeVerifier } from "./pkce";
import { env } from "../../../shared/constants/env";
// Imports statiques sûrs : ni sessionStore ni socketClient ne réimportent ce
// module, donc aucun cycle. Cf. la note sur performLogout plus bas.
import { useSessionStore } from "../../../shared/stores/sessionStore";
import { disconnectSocket } from "../../../shared/realtime/socketClient";

// ─── Types ────────────────────────────────────────────────────────────────────

// Aligné sur `KeycloakTokenResponse` du nouveau openapi.json :
//   - `id_token` n'est plus dans le schéma documenté → optionnel
//   - `session_state`, `scope` sont ajoutés par Keycloak (info OIDC standard)
//
// On garde `id_token` optionnel pour rester compatible si le backend l'envoie
// encore en plus (champ non-documenté mais présent), sans casser si absent.
export type TokenResponse = {
  access_token:        string;
  refresh_token:       string;
  expires_in:          number;
  // 0 = offline session (expiry contrôlée par SSO Session Max côté Keycloak)
  refresh_expires_in:  number;
  // Champs additionnels retournés par /auth/keycloak/exchange.
  id_token?:           string;
  session_state?:      string;
  scope?:              string;
  token_type?:         string;
  // UUID interne user-service ajouté par identity-service (depuis openapi v3).
  // Préférer ce champ à payload.sub pour positionner userId en session.
  user_id?:            string;
};

export type AuthParams = {
  codeVerifier:     string;
  state:            string;
  authorizationUrl: string;
};

type PkceSession = { codeVerifier: string; state: string };

// ─── PKCE session — persistance sessionStorage ───────────────────────────────
//
// Sur natif, le code_verifier vivait dans SecureStore en secours du useRef de
// l'écran, au cas où l'OS recycle l'app entre startAuth et callback.
//
// Sur web, ce n'est plus un secours mais LE mécanisme : le login PKCE quitte
// complètement la page pour Keycloak, puis y revient. Toute mémoire JS est
// détruite entre les deux. Sans persistance, l'échange code → token est
// impossible.
//
// sessionStorage plutôt que localStorage : le verifier est lié à UNE tentative
// de connexion dans UN onglet. Il ne doit pas fuiter vers les autres onglets ni
// survivre à la fermeture du navigateur.
//
// ⚠️ Le chiffrement OS du Keychain/Keystore n'a pas d'équivalent : le verifier
// est lisible par une XSS pendant la fenêtre du login. C'est le modèle admis
// pour un client public SPA (cf. OAuth 2.0 for Browser-Based Apps), et la
// raison pour laquelle on le supprime dès l'échange effectué.

const PKCE_VERIFIER_KEY = "sosmain.pkce_verifier";
const PKCE_STATE_KEY = "sosmain.pkce_state";

export async function savePkceSession(codeVerifier: string, state: string): Promise<void> {
  try {
    sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
    sessionStorage.setItem(PKCE_STATE_KEY, state);
  } catch {
    // Stockage bloqué (navigation privée stricte) : le login échouera au retour
    // de callback avec une erreur explicite plutôt qu'ici.
  }
}

export async function loadPkceSession(): Promise<PkceSession | null> {
  try {
    const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    const state = sessionStorage.getItem(PKCE_STATE_KEY);
    if (!codeVerifier || !state) return null;
    return { codeVerifier, state };
  } catch {
    return null;
  }
}

export async function clearPkceSession(): Promise<void> {
  try {
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_STATE_KEY);
  } catch {
    /* silencieux */
  }
}

// ─── Callback URL parser — 3 stratégies en cascade ───────────────────────────

export function parseCallbackUrl(url: string): {
  code?:             string;
  state?:            string;
  error?:            string;
  errorDescription?: string;
} {
  const qIdx = url.indexOf("?");
  const hIdx = url.indexOf("#");

  const queryStr = qIdx !== -1 ? url.slice(qIdx + 1, hIdx !== -1 ? hIdx : undefined) : "";
  const fragStr  = hIdx  !== -1 ? url.slice(hIdx  + 1) : "";

  // Stratégie 1 : URLSearchParams standard (query puis fragment)
  for (const src of [queryStr, fragStr]) {
    if (!src) continue;
    try {
      const p = new URLSearchParams(src);
      const code  = p.get("code");
      const state = p.get("state");
      const error = p.get("error");
      if (code || error) {
        return {
          code:             code  ?? undefined,
          state:            state ?? undefined,
          error:            error ?? undefined,
          errorDescription: p.get("error_description") ?? undefined,
        };
      }
    } catch { /* continue */ }
  }

  // Stratégie 2 : regex sur l'URL complète (query + fragment)
  const pick = (re: RegExp): string | undefined => {
    const m = re.exec(url);
    return m ? decodeURIComponent(m[1]) : undefined;
  };
  return {
    code:             pick(/[?&#]code=([^&\s#]+)/i),
    state:            pick(/[?&#]state=([^&\s#]+)/i),
    error:            pick(/[?&#]error=([^&\s#]+)/i),
    errorDescription: pick(/[?&#]error_description=([^&\s#]+)/i),
  };
}

// ─── Auth URL builder (Keycloak direct, le user log in sur Keycloak) ─────────

export function buildAuthorizationUrl(): AuthParams {
  const codeVerifier  = generateCodeVerifier();
  const codeChallenge = generateCodeChallenge(codeVerifier);

  // state : 16 octets hex — protection CSRF (CSPRNG strict, voir pkce.ts)
  // Le polyfill `react-native-get-random-values` n'a plus lieu d'être : la Web
  // Crypto API est native au navigateur. Elle n'est en revanche exposée QUE sur
  // un contexte sécurisé — d'où ce message, qui pointe la seule cause réaliste.
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error(
      "[keycloak] crypto.getRandomValues indisponible — l'app doit être servie en HTTPS (ou depuis localhost). Le login PKCE est impossible sur une origine non sécurisée.",
    );
  }
  const stateBytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(stateBytes);
  const state = Array.from(stateBytes).map((b) => b.toString(16).padStart(2, "0")).join("");

  const params = new URLSearchParams({
    response_type:         "code",
    client_id:             keycloakConfig.clientId,
    redirect_uri:          keycloakConfig.redirectUri,
    scope:                 keycloakConfig.scopes.join(" "),
    code_challenge:        codeChallenge,
    code_challenge_method: "S256",
    state,
    // `prompt=login` force Keycloak à RE-DEMANDER les identifiants à chaque
    // autorisation, même si une session SSO existe encore. Sans ça, après un
    // logout app, relancer le login ré-émettrait un code silencieusement →
    // reconnexion automatique non désirée.
    //
    // ⚠️ Sur web, ce choix mérite d'être rediscuté au Lot 5 : le cookie SSO vit
    // désormais dans le navigateur de l'utilisateur, partagé avec les autres
    // applications du realm. `prompt=login` casse donc le SSO inter-applicatif,
    // ce qui n'avait aucun coût dans une WebView isolée mais devient visible
    // ici. Le conserver reste défendable (l'app manipule de l'argent), mais
    // c'est un arbitrage produit, plus une simple conséquence technique.
    prompt:                "login",
  });

  return {
    codeVerifier,
    state,
    authorizationUrl: `${keycloakConfig.authorizationEndpoint}?${params.toString()}`,
  };
}

// ─── Token exchange via Gateway (POST /auth/keycloak/exchange) ───────────────
// Le code OAuth obtenu sur Keycloak est échangé contre les JWT SOS via le Gateway.
// Le Gateway provisionne l'utilisateur côté User Service + Wallet au passage.

const EXCHANGE_TIMEOUT_MS = 15_000;

export async function exchangeCodeForTokens(
  code:         string,
  codeVerifier: string,
): Promise<TokenResponse> {
  // AbortController garantit un timeout dur — sans ça, fetch() peut pendre
  // indéfiniment sur un réseau lent (le user reste sur la WebView avec un
  // spinner sans feedback d'erreur).
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS);

  try {
    const res = await fetch(`${env.gatewayUrl}/auth/keycloak/exchange`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        redirect_uri:  keycloakConfig.redirectUri,
        code_verifier: codeVerifier,
      }),
      signal: controller.signal,
    });

    if (!res.ok) {
      const detail = await res.text().catch(() => String(res.status));
      throw new Error(`Token exchange: ${res.status} — ${detail}`);
    }
    return res.json() as Promise<TokenResponse>;
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") {
      throw new Error("Token exchange timed out");
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

// ─── Token refresh — singleton + retry, via Gateway ──────────────────────────
// Singleton : si un refresh est en cours, tous les appelants (interceptor Axios,
// token watcher) reçoivent la même promesse → un seul appel réseau.
// Retry avec back-off exponentiel pour les erreurs réseau transitoires.

let activeRefresh: Promise<TokenResponse> | null = null;

export async function refreshAccessTokens(refreshToken: string): Promise<TokenResponse> {
  if (activeRefresh) return activeRefresh;

  activeRefresh = (async (): Promise<TokenResponse> => {
    let lastErr: unknown;

    for (let attempt = 0; attempt <= TOKEN_CONFIG.REFRESH_MAX_RETRIES; attempt++) {
      try {
        const res = await fetch(`${env.gatewayUrl}/auth/refresh`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!res.ok) {
          const detail = await res.text().catch(() => String(res.status));
          // 4xx = token invalide/révoqué → pas la peine de retry
          if (res.status >= 400 && res.status < 500) {
            throw new Error(`Refresh non-recoverable: ${res.status} — ${detail}`);
          }
          throw new Error(`Refresh: ${res.status} — ${detail}`);
        }

        // Le Gateway peut renvoyer seulement { access_token, expires_in } sur refresh ;
        // on conserve le refresh_token courant si absent de la réponse.
        const json = await res.json() as Partial<TokenResponse>;
        return {
          access_token:       json.access_token       ?? "",
          refresh_token:      json.refresh_token      ?? refreshToken,
          expires_in:         json.expires_in         ?? 1800,
          refresh_expires_in: json.refresh_expires_in ?? 0,
          id_token:           json.id_token,
          session_state:      json.session_state,
          scope:              json.scope,
          token_type:         json.token_type,
        };
      } catch (e) {
        lastErr = e;
        // Erreur 4xx → inutile de retenter
        if (e instanceof Error && e.message.startsWith("Refresh non-recoverable")) throw e;
        if (attempt < TOKEN_CONFIG.REFRESH_MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        }
      }
    }
    throw lastErr;
  })().finally(() => { activeRefresh = null; });

  return activeRefresh;
}

// ─── Logout via Gateway (POST /auth/logout, Bearer requis) ───────────────────
// Révoque le token côté Identity Service (Redis deny-list) ET côté Keycloak.

export async function revokeToken(accessToken: string): Promise<void> {
  for (let attempt = 0; attempt <= TOKEN_CONFIG.REVOKE_MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TOKEN_CONFIG.REVOKE_TIMEOUT);
    try {
      const res = await fetch(`${env.gatewayUrl}/auth/logout`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${accessToken}`,
        },
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (env.isDev) console.log("[auth] Gateway logout HTTP status:", res.status);
      // 200/204 = succès. 401 = token déjà invalide → traiter comme succès.
      if (res.ok || res.status === 204 || res.status === 401) return;
      const body = await res.text().catch(() => "");
      throw new Error(`Logout: ${res.status} — ${body}`);
    } catch (e) {
      clearTimeout(timer);
      if (attempt < TOKEN_CONFIG.REVOKE_MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      } else {
        throw e;
      }
    }
  }
}

// ─── Token introspect (GET /auth/introspect) ─────────────────────────────────
// Validation SERVEUR d'un token : détecte la révocation côté Keycloak (ce que le
// simple check d'exp local ne peut pas faire). Important pour vérifier la
// validité réelle d'un token persisté au boot.

export type IntrospectResult = {
  active: boolean;
  sub?:   string;
  email?: string;
  roles?: string[];
  exp?:   number;
};

// Shape réelle renvoyée par le Gateway (enveloppe { success, data: { active, claims } })
type IntrospectClaims = {
  sub?:          string;
  email?:        string;
  exp?:          number;
  realm_access?: { roles?: string[] };
};
type IntrospectBody = {
  // Réponse plate (ancienne version ou endpoint direct)
  active?: boolean;
  // Réponse enveloppée { success, data: { active, claims } }
  data?: {
    active?: boolean;
    claims?: IntrospectClaims;
  };
};

export async function introspectToken(accessToken: string): Promise<IntrospectResult> {
  const res = await fetch(`${env.gatewayUrl}/auth/introspect`, {
    method:  "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (env.isDev) {
    const body = await res.clone().text().catch(() => "<no-body>");
    console.log("[introspect]", { status: res.status, ok: res.ok, body: body.slice(0, 200) });
  }

  if (res.status === 401) return { active: false };
  if (!res.ok) throw new Error(`Introspect: ${res.status}`);

  const json = (await res.json()) as IntrospectBody;

  // Le Gateway enveloppe la réponse dans { success, data: { active, claims } }.
  // fetch() brut ne passe PAS par l'intercepteur Axios → on désenveloppe manuellement.
  const inner  = json.data ?? json;
  const claims = (json.data?.claims) ?? ({} as IntrospectClaims);

  return {
    active: inner.active ?? false,
    sub:    claims.sub,
    email:  claims.email,
    exp:    claims.exp,
    roles:  claims.realm_access?.roles,
  };
}

// ─── Logout complet ───────────────────────────────────────────────────────────
// 1. Révocation Gateway (fire-and-forget, non-bloquant)
// 2. Clear PKCE storage
// 3. Clear session locale (synchrone → RootNavigator re-route immédiatement)

// ⚠️ PORTAGE : `require()` n'existe pas en ESM/Vite.
//
// La version native s'en servait pour charger paresseusement les modules de
// nettoyage — d'une part pour éviter des cycles d'import, d'autre part parce
// qu'un module absent (feature non chargée) devait simplement être ignoré.
//
// Le remplacement fidèle est `await import()`, mais il est ASYNCHRONE, alors
// que `performLogout` doit rester synchrone : c'est `clearSession()` qui coupe
// l'interface, et il ne doit dépendre d'aucune promesse.
//
// D'où la structure ci-dessous : la partie critique reste synchrone (import
// statique de sessionStore, qui ne crée aucun cycle), et les nettoyages
// best-effort partent en `void import().then()`. Ils ne peuvent donc plus
// empêcher ni retarder la déconnexion — ce qui était déjà l'intention d'origine.
//
// agentStore reste en import dynamique pour une VRAIE raison de cycle :
// keycloakAuthService → agentStore → agentService → api/client → keycloakAuthService.
export function performLogout(): void {
  // On lit accessToken + userId AVANT de purger, puis on PURGE EN PREMIER :
  // clearSession() est l'action critique (déconnexion UI). On la fait avant tout
  // travail best-effort pour qu'AUCUNE erreur réseau/module ne puisse l'empêcher.
  // revokeToken reçoit le token CAPTURÉ → indépendant de l'état.
  const { accessToken, clearSession } = useSessionStore.getState();

  clearSession(); // synchrone → les gardes de route redirigent immédiatement
  void clearPkceSession(); // nettoyage sessionStorage, non-bloquant

  // ─── Nettoyages best-effort (ne peuvent plus empêcher la déconnexion) ───────

  // Cleanup Socket.IO (évite de garder une connexion authentifiée fantôme).
  disconnectSocket();

  // Reset du runtime agent (status, currentMissionId) — évite la fuite de
  // contexte si Agent A se déconnecte puis Agent B se connecte.
  void import("../../agent/stores/agentStore")
    .then((m) => m.useAgentStore.getState().clearRuntime?.())
    .catch(() => {
      /* module agent pas chargé (rôle client uniquement) */
    });

  // Retire l'abonnement Web Push du backend. On passe le token CAPTURÉ
  // explicitement : la session est déjà purgée, l'intercepteur ne fournirait
  // plus le Bearer. Import dynamique pour ne pas tirer le module de push (et
  // son accès au Service Worker) dans le chemin critique du logout.
  void import("../../notifications/webPush")
    .then((m) => m.unregisterWebPush(accessToken))
    .catch(() => {
      /* feature notifications pas chargée — on ignore */
    });

  // Sépare aussi la session du chat tiers. Sans ce nettoyage, deux personnes
  // utilisant successivement le même navigateur pourraient partager le même
  // historique tawk.to. Best effort : le logout Alomain reste immédiat.
  void import("../../support/services/tawkService")
    .then((m) => m.resetTawkSession())
    .catch(() => {
      /* widget jamais chargé ou service tiers indisponible */
    });

  if (accessToken) {
    if (env.isDev) console.log("[auth] Envoi de la requête logout au Gateway…");
    revokeToken(accessToken)
      .then(() => {
        if (env.isDev) console.log("[auth] ✅ Session révoquée côté Gateway");
      })
      .catch((e) => {
        if (env.isDev) console.warn("[auth] ⚠️ Logout Gateway échoué (session locale clear quand même) :", e);
      });
  } else if (env.isDev) {
    console.warn("[auth] Pas d'access token → logout local seulement");
  }
}
